/* ============================================
   AKI — CART (Shopify Ajax Cart API)
   Uses the storefront's built-in cart endpoints:
   /cart/add.js · /cart/change.js · /cart.js
   and the Section Rendering API to refresh the
   cart page without a full reload.
   ============================================ */

const AkiCart = (() => {
  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  function updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.toggleAttribute('hidden', cart.item_count === 0);
    });
  }

  async function addToCart(variantId, quantity, button) {
    if (!variantId || (button && button.dataset.busy)) return;
    if (button) button.dataset.busy = 'true';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Could not add item to cart');
      }

      const cart = await fetchCart();
      updateCartCount(cart);

      if (button) {
        const original = button.dataset.label || button.textContent;
        button.dataset.label = original;
        button.textContent = 'Added ✓';
        button.classList.add('btn--added');
        window.setTimeout(() => {
          button.textContent = original;
          button.classList.remove('btn--added');
          delete button.dataset.busy;
        }, 1400);
      }
    } catch (err) {
      console.error('aki: add to cart failed', err);
      if (button) {
        button.textContent = 'Ausverkauft';
        window.setTimeout(() => {
          button.textContent = button.dataset.label || button.textContent;
          delete button.dataset.busy;
        }, 1800);
      }
    }
  }

  /* Refresh the cart page via the Section Rendering API
     https://shopify.dev/docs/api/liquid/render-section */
  async function refreshCartSections() {
    const url = `${window.location.pathname}?sections=main-cart-items,main-cart-footer`;
    const res = await fetch(url);
    const data = await res.json();

    const itemsEl = document.querySelector('[data-cart-items-section]');
    if (itemsEl && data['main-cart-items']) {
      const doc = new DOMParser().parseFromString(data['main-cart-items'], 'text/html');
      const next = doc.querySelector('[data-cart-items-section]');
      if (next) itemsEl.replaceWith(next);
    }

    const footerEl = document.querySelector('[data-cart-footer-section]');
    if (footerEl && data['main-cart-footer']) {
      const doc = new DOMParser().parseFromString(data['main-cart-footer'], 'text/html');
      const next = doc.querySelector('[data-cart-footer-section]');
      if (next) footerEl.replaceWith(next);
    }

    bindCartLineEvents();
    const cart = await fetchCart();
    updateCartCount(cart);
  }

  async function changeLine(line, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    await refreshCartSections();
  }

  function bindCartLineEvents() {
    document.querySelectorAll('[data-cart-line]').forEach((row) => {
      const line = Number(row.dataset.line);
      const qtyEl = row.querySelector('[data-qty]');

      row.querySelector('[data-qty-increase]')?.addEventListener('click', () => {
        changeLine(line, Number(qtyEl.textContent) + 1);
      });
      row.querySelector('[data-qty-decrease]')?.addEventListener('click', () => {
        const next = Number(qtyEl.textContent) - 1;
        changeLine(line, Math.max(0, next));
      });
      row.querySelector('[data-remove-line]')?.addEventListener('click', () => {
        changeLine(line, 0);
      });
    });
  }

  function bindAddToCartButtons() {
    document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const form = btn.closest('form');
        const variantId = btn.dataset.variantId || form?.querySelector('[name="id"]')?.value;
        addToCart(variantId, 1, btn);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindAddToCartButtons();
    bindCartLineEvents();
    fetchCart().then(updateCartCount);
  });

  return { addToCart, changeLine, updateCartCount };
})();
